import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { ChoirEvent } from '../../types';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { eventDotClass, eventTitle, eventTypeClass, eventTypeShort, formatShort, monthName, parseDate, weekdayShort } from '../../utils/labels';
const dnevi = ['pon', 'tor', 'sre', 'čet', 'pet', 'sob', 'ned'];
interface MonthCalendarProps {
  events: ChoirEvent[];
  detailBase: string;
  today: string;
}
export function MonthCalendar({
  events,
  detailBase,
  today
}: MonthCalendarProps) {
  const zacetni = parseDate(today);
  const [leto, setLeto] = useState(zacetni.getFullYear());
  const [mesec, setMesec] = useState(zacetni.getMonth());
  const dogodkiPoDnevu = useMemo(() => {
    const map: Record<string, ChoirEvent[]> = {};
    events.forEach(e => {
      map[e.datum] = [...(map[e.datum] ?? []), e];
    });
    return map;
  }, [events]);
  const prvi = new Date(leto, mesec, 1);
  const offset = (prvi.getDay() + 6) % 7;
  const dniVMesecu = new Date(leto, mesec + 1, 0).getDate();
  const celice: (number | null)[] = [...Array(offset).fill(null), ...Array.from({
    length: dniVMesecu
  }, (_, i) => i + 1)];
  while (celice.length % 7 !== 0) celice.push(null);
  const vMesecu = events.filter(e => {
    const d = parseDate(e.datum);
    return d.getFullYear() === leto && d.getMonth() === mesec;
  }).sort((a, b) => a.datum.localeCompare(b.datum));
  function premakni(delta: number) {
    const d = new Date(leto, mesec + delta, 1);
    setLeto(d.getFullYear());
    setMesec(d.getMonth());
  }
  const key = (d: number) => `${leto}-${String(mesec + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return <div className="grid gap-4 lg:grid-cols-[1.15fr_1fr]" data-id="element-351" data-mp="2ln3ldtkrhw3a">
      <Card className="p-4 sm:p-5" data-id="element-352" data-mp="2lkhfrzhflqnp">
        <div className="flex items-center justify-between" data-id="element-353" data-mp="2pn4bibh83vq6">
          <h2 className="text-base font-semibold capitalize text-ink" data-id="element-354" data-mp="2plje8e3ixyom">
            {monthName(mesec)} {leto}
          </h2>
          <div className="flex gap-1" data-id="element-355" data-mp="lkb0382om3fa">
            <button onClick={() => premakni(-1)} aria-label="Prejšnji mesec" className="rounded-lg border border-line p-1.5 text-ink-muted transition-colors duration-150 ease-out hover:bg-canvas hover:text-ink" data-id="element-356" data-mp="llm6zgrfbram">
              <ChevronLeftIcon className="h-4 w-4" data-id="element-357" data-mp="1993zxfmfcvf6" />
            </button>
            <button onClick={() => premakni(1)} aria-label="Naslednji mesec" className="rounded-lg border border-line p-1.5 text-ink-muted transition-colors duration-150 ease-out hover:bg-canvas hover:text-ink" data-id="element-358" data-mp="14aj37q6dstvh">
              <ChevronRightIcon className="h-4 w-4" data-id="element-359" data-mp="1e9i06bhsb12n" />
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[11px] font-medium uppercase tracking-wide text-ink-muted" data-id="element-360" data-mp="3k1yizhi8ouxa">
          {dnevi.map(d => <span key={d} className="py-1" data-id="element-361" data-mp="35vwn4twxijf2">
              {d}
            </span>)}
        </div>

        <div className="mt-1 grid grid-cols-7 gap-1" data-id="element-362" data-mp="39woutiq5kg98">
          {celice.map((d, i) => {
          if (d === null) return <span key={`p${i}`} className="h-16 rounded-lg" data-id="element-363" data-mp="2w5ttcrhzh1fo" />;
          const iso = key(d);
          const dnevni = dogodkiPoDnevu[iso] ?? [];
          const jeDanes = iso === today;
          return <div key={iso} className={`h-16 rounded-lg border p-1.5 text-left ${jeDanes ? 'border-green-300 bg-green-50' : 'border-transparent bg-canvas/60'}`} data-id="element-364" data-mp="2m6ukb7tb33s5" data-mpi={i}>
                <span className={`text-[12px] font-medium ${jeDanes ? 'text-green-700' : 'text-ink'}`} data-id="element-365" data-mp="2i627y5p50irn">
                  {d}
                </span>
                <div className="mt-1 space-y-0.5" data-id="element-366" data-mp="bgvzywh5xwzj">
                  {dnevni.slice(0, 2).map(e => <Link key={e.id} to={`${detailBase}/${e.id}`} className="flex items-center gap-1 truncate rounded px-0.5 text-[10px] text-ink-muted hover:text-ink" data-id="element-367" data-mp="gefn6lvu9k3m">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${eventDotClass[e.type]}`} data-id="element-368" data-mp="22jef1qkm76by" />
                      <span className="truncate" data-id="element-369" data-mp="1yhj3lt8yzckg">{eventTypeShort[e.type]}</span>
                    </Link>)}
                  {dnevni.length > 2 && <span className="block px-0.5 text-[10px] text-ink-muted" data-id="element-370" data-mp="1ot17vqfjrvno">+{dnevni.length - 2}</span>}
                </div>
              </div>;
        })}
        </div>
      </Card>

      <Card className="p-5" data-id="element-371" data-mp="1ev3iv0pv5uu0">
        <h2 className="text-base font-semibold text-ink" data-id="element-372" data-mp="1evwgnc6uha00">Agenda</h2>
        <p className="mt-0.5 text-sm capitalize text-ink-muted" data-id="element-373" data-mp="1athzqxmqkkbd">
          {monthName(mesec)} {leto}
        </p>
        {vMesecu.length === 0 ? <p className="mt-6 text-center text-[13px] text-ink-muted" data-id="element-374" data-mp="15uovlvtca5nv">Ta mesec ni dogodkov.</p> : <ul className="mt-3 divide-y divide-line" data-id="element-375" data-mp="3hjcxw26kuyyr">
            {vMesecu.map(e => <li key={e.id} data-id="element-376" data-mp="3meild5fmoloy">
                <Link to={`${detailBase}/${e.id}`} className="flex items-center gap-3 py-3 transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-377" data-mp="3qhx96i9t66cy">
                  <span className="w-16 shrink-0 text-[13px] font-medium text-ink-muted" data-id="element-378" data-mp="2yr0y9n7b6bsb">
                    {formatShort(e.datum)} {weekdayShort(e.datum)}
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-379" data-mp="32pzzawpx5oeh">
                    <span className="block truncate text-sm font-medium text-ink" data-id="element-380" data-mp="37qe7qfvfn0d1">{eventTitle(e)}</span>
                    <span className="block truncate text-[13px] text-ink-muted" data-id="element-381" data-mp="3cixm3f1y1ziw">
                      {e.zacetek} · {e.lokacija}
                    </span>
                  </span>
                  <Badge className={eventTypeClass[e.type]} data-id="element-382" data-mp="2k134mrqm0tvq">{eventTypeShort[e.type]}</Badge>
                </Link>
              </li>)}
          </ul>}
      </Card>
    </div>;
}